import React, { useState } from 'react';
import { Constants, EagerdogErrors } from "@eagerdog/constants";
import { IEvent } from "@eagerdog/interfaces";

import ContextMenu/*, { IContextMenuOption }*/ from "src/components/ContextMenu/ContextMenu";
import { toast } from "src/components/Toast/ToastManager";
import { Modal, useModal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";
import DatePick from "src/components/DatePick/DatePick";
import BatchApproveModal from "./BatchApproveModal";

import { apiService } from "../../../services";
import { useMessages } from "src/context/MessagesContext";
import type { CustomCellRendererProps } from 'ag-grid-react';

import styles from "./WaitlistTab.module.scss";

interface CustomUserWaitlistCellRendererProps extends CustomCellRendererProps {
  getWaitlists(): void;
  event?: IEvent;
}

export enum ConfirmModalType {
  Delete = "delete",
  Cancel = "cancel",
  ApproveWithoutPayment = "approve_without_payment",
  EditDateReceived = "edit_date_received",
}

export const ActionsRenderer = (props: CustomUserWaitlistCellRendererProps) => {
  const { isShown, toggle } = useModal();
  const { initiateConversation } = useMessages();
  const [modalContent, setModalContent] = useState<ConfirmModalType | null>(null)
  const [approvingWithoutPayment, setApprovingWithoutPayment] = useState<boolean>(false);
  // Set when the server refused Approve Without Payment because the show is full;
  // the next Confirm re-submits with allow_over_capacity.
  const [overCapacityReason, setOverCapacityReason] = useState<string | null>(null);
  const [affectedWaitlists, setAffectedWaitlists] = useState<any[]>([]);
  const [selectedForApproval, setSelectedForApproval] = useState<Set<string>>(new Set());
  const [isSavingDateReceived, setIsSavingDateReceived] = useState<boolean>(false);
  const [editDateReceived, setEditDateReceived] = useState<Date | undefined>(
    props.value?.waitlist?.date_received ? new Date(props.value.waitlist.date_received) : undefined
  );
  const [batchApproveOpen, setBatchApproveOpen] = useState<boolean>(false);

  if (!props.value?.waitlist) {
    return null;
  }

  const options = []

  const openApproveWithoutPaymentModal = () => {
    apiService.previewApproveWaitlistWithoutPayment(
      props.value.waitlist.event_id,
      props.value.waitlist._id
    )
      .then((waitlists) => {
        setAffectedWaitlists(waitlists || []);
        // Default to including everything pending on this transaction (today's
        // behavior), but let the secretary deselect siblings - e.g. a dog with
        // two pending runs on the same transaction where only one has a
        // vacancy open. The anchor entry (the one "Approve" was clicked on)
        // can't be deselected.
        setSelectedForApproval(
          new Set(
            (waitlists || [])
              .map((w: any) => w._id.toString())
              .filter((id: string) => id !== props.value.waitlist._id.toString())
          )
        );
        setModalContent(ConfirmModalType.ApproveWithoutPayment);
        toggle();
      })
      .catch((error) => {
        toast.show({
          title: "Approve Without Payment",
          content: error.response?.data?.message ?? "Something went wrong, please try again later",
          duration: 10000,
          type: "fail"
        });
      });
  };

  const resendConfirmationEmailOption = {
    label: "Re-send Confirmation Email",
    onClick: () => {
      apiService.resendLotteryConfirmationEmail(props.value.waitlist.event_id, props.value.waitlist._id)
        .then(() => {
          toast.show({
            title: "Re-send Confirmation Email",
            content: "Confirmation email has been resent to the owner of " + props.value.waitlist.call_name + " (" + props.value.waitlist.registered_name + "). If they have more than one pending lottery entry for this event, a confirmation was resent for each.",
            duration: 10000,
            type: "success"
          });
        })
        .catch((error) => {
          toast.show({
            title: "Re-send Confirmation Email",
            content: error.response?.data?.message ?? "Something went wrong, please try again later",
            duration: 10000,
            type: "fail"
          });
        });
    }
  };

  if (props.value.waitlist.status === Constants.waitlist_status.waitlist) {
    options.push({
      label: "Edit Date Received",
      onClick: () => {
        setEditDateReceived(
          props.value.waitlist.date_received
            ? new Date(props.value.waitlist.date_received)
            : undefined
        );
        setModalContent(ConfirmModalType.EditDateReceived);
        toggle();
      }
    });

    if (props.value.waitlist.lottery_eligible) {
      options.push(resendConfirmationEmailOption);
    }

    options.push({
      label: "Approve",
      onClick:() => {
        // Always route through the batch-approve modal for entries awaiting
        // approval — including lottery-eligible ones. This lets the admin see
        // and optionally include the owner's other pending entries so they're
        // combined into a single charge, instead of firing one separate
        // off-session card charge per "Approve" click (which can trip
        // issuer/Radar fraud protection when several similar charges hit the
        // same card in quick succession).
        setBatchApproveOpen(true);
      }
    })

    const canApproveWithoutPayment =
      !props.value.waitlist.lottery_eligible &&
      props.value.waitlist.transaction_registration_kind === Constants.registration_kind.owner;

    if (canApproveWithoutPayment) {
      options.push({
        label: "Approve Without Payment",
        onClick: () => openApproveWithoutPaymentModal(),
      });
    }

    options.push({
      label: "Cancel",
      onClick:() => {
        setModalContent(ConfirmModalType.Cancel);
        toggle();
      }
    })
  }

  // A lottery invite that went unpaid for 72 hours. Nothing is re-invited automatically;
  // the secretary can invite it again or cancel it.
  if (props.value.waitlist.status === Constants.waitlist_status.expired) {
    options.push({
      label: "Approve",
      onClick: () => { setBatchApproveOpen(true); }
    });

    options.push({
      label: "Cancel",
      onClick: () => {
        setModalContent(ConfirmModalType.Cancel);
        toggle();
      }
    });
  }

  if (
    props.value.waitlist.status === Constants.waitlist_status.canceled ||
    props.value.waitlist.status === Constants.waitlist_status.declined
  ) {
    options.push({
      label: "Delete",
      onClick:() => {
        setModalContent(ConfirmModalType.Delete);
        toggle();
      }
    })
  }

  if (props.value.waitlist.status === Constants.waitlist_status.awaiting_payment) {
    if (props.value.waitlist.lottery_eligible) {
      options.push(resendConfirmationEmailOption);

      // A lottery entry already selected/approved (i.e. sitting here awaiting
      // payment) can be finalized without a charge — e.g. it was entered with a
      // 100%-off discount code and there's nothing left to actually collect.
      // This does NOT skip the draw itself; only already-approved entries qualify.
      options.push({
        label: "Approve Without Payment",
        onClick: () => openApproveWithoutPaymentModal(),
      });
    }

    options.push({
      label: "Re-send Payment Email",
      onClick: () => {
        apiService.resendWaitlistApprovalEmail(props.value.waitlist.event_id, props.value.waitlist._id)
          .then(() => {
            toast.show({
              title: "Re-send Payment Email",
              content: "Payment approval email has been resent to " + props.value.waitlist.call_name + " (" + props.value.waitlist.registered_name + ")",
              duration: 10000,
              type: "success"
            });
          })
          .catch((error) => {
            toast.show({
              title: "Re-send Payment Email",
              content: error.response?.data?.message ?? "Something went wrong, please try again later",
              duration: 10000,
              type: "fail"
            });
          });
      }
    });

    options.push({
      label: "Move to Waitlist",
      onClick: () => {
        apiService.returnAwaitingWaitlistToWaitlist(props.value.waitlist.event_id, props.value.waitlist._id)
          .then(() => {
            props.getWaitlists()
            toast.show({
              title: "Move to Waitlists",
              content: "You've successfully returned back the entry to the waitlists",
              duration: 10000,
              type: "success"
            });
          })
          .catch((error) => {
            toast.show({
              title: "Move to Waitlists",
              content: error.response.data.message ??  "Something went wrong, please try again later",
              duration: 10000,
              type: "fail"
            });
          })
      }
    })
  }

  // Always available regardless of status so secretaries can reach the owner
  // directly from the waitlist. Uses user_id directly since it's already on
  // the waitlist record (no need to resolve owner from a dog).
  if (props.value.waitlist.user_id) {
    options.unshift({
      label: "Send Message",
      onClick: () => {
        initiateConversation(props.value.waitlist.user_id, props.value.waitlist.owner_name, true);
      }
    });
  }

  const formatWaitlistEntry = (w: any): string => {
    return [
      w.call_name,
      [w.show_type, w.show_name].filter(Boolean).join(" "),
      w.show_date ? new Date(w.show_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "",
      [
        w.dog_class?.show_element || "",
        w.dog_class?.level || "",
        w.dog_class?.section || "",
      ].filter(Boolean).join(", "),
    ].filter(Boolean).join(" - ");
  };

  const toggleSelectedForApproval = (id: string) => {
    setSelectedForApproval((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getMessage = () => {
    if (modalContent === ConfirmModalType.ApproveWithoutPayment) {
      const anchorId = props.value.waitlist._id.toString();
      const otherWaitlists = affectedWaitlists.filter((w: any) => w._id.toString() !== anchorId);

      if (overCapacityReason) {
        return (
          <div>
            <p className={styles.batchApproveCapacityOver}>{overCapacityReason}</p>
            <p className={styles.batchApproveHint}>
              Approving anyway will put the show over its limit. Online entries will stay closed. Confirm to approve anyway.
            </p>
          </div>
        );
      }

      if (!otherWaitlists.length) {
        return <p>Approve {formatWaitlistEntry(props.value.waitlist)} without collecting payment?</p>;
      }

      return (
        <div>
          <p className={styles.batchApproveOthersLabel}>
            This dog has other pending entries on the same transaction. Uncheck any that shouldn't be approved right now (e.g. no vacancy yet):
          </p>
          <ul className={styles.batchApproveList}>
            <li className={styles.batchApproveItem}>
              <label className={styles.batchApproveCheckbox}>
                <input type="checkbox" checked disabled />
                <span>{formatWaitlistEntry(props.value.waitlist)}</span>
              </label>
            </li>
            {otherWaitlists.map((w: any) => (
              <li key={w._id.toString()} className={styles.batchApproveItem}>
                <label className={styles.batchApproveCheckbox}>
                  <input
                    type="checkbox"
                    checked={selectedForApproval.has(w._id.toString())}
                    onChange={() => toggleSelectedForApproval(w._id.toString())}
                  />
                  <span>{formatWaitlistEntry(w)}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (modalContent === ConfirmModalType.Cancel) {
      return <p>Are you sure you want to cancel {props.value.waitlist.call_name} ({props.value.waitlist.registered_name})'s waitlist registration to {props.value.waitlist.show_type} {props.value.waitlist.show_name} ({props.value.waitlist.dog_class.show_element}, {(props.value.waitlist.dog_class.level || "No Level")})? {props.value.waitlist.lottery_eligible ? "" : ""}</p>
    }
    return <p>Are you sure you want to delete {props.value.waitlist.call_name} ({props.value.waitlist.registered_name})'s waitlist registration to {props.value.waitlist.show_type} {props.value.waitlist.show_name} ({props.value.waitlist.dog_class.show_element}, {(props.value.waitlist.dog_class.level || "No Level")})?</p>
  }

  const getModalContent = () => {
    if (modalContent === ConfirmModalType.EditDateReceived) {
      return (
        <div className={styles.editDateReceivedModal}>
          <div className={styles.editDateReceivedTitle}>Edit Date Received</div>
          <p className={styles.editDateReceivedSubtitle}>
            {props.value.waitlist.call_name} — {props.value.waitlist.show_type} {props.value.waitlist.show_name}
          </p>
          <div className={styles.editDateReceivedField}>
            <DatePick
              label="Date Received (MM/DD/YYYY)"
              value={editDateReceived}
              onChange={(date: Date) => { setEditDateReceived(date); }}
            />
            <p className={styles.editDateReceivedHelper}>
              Select the date this entry was received. Waitlist positions will be reordered accordingly and affected owners will be notified.
            </p>
          </div>
          <div className={styles.editDateReceivedActions}>
            <button
              className={styles.cancelBtn}
              onClick={() => { setModalContent(null); toggle(); }}
            >
              Cancel
            </button>
            <button
              className={styles.saveBtn}
              disabled={isSavingDateReceived || !editDateReceived}
              onClick={() => {
                if (!editDateReceived) return;
                setIsSavingDateReceived(true);
                apiService.updateWaitlistDateReceived(
                  props.value.waitlist.event_id,
                  props.value.waitlist._id,
                  editDateReceived
                )
                  .then(() => {
                    toast.show({
                      title: "Date Received Updated",
                      content: "Date received updated and waitlist positions adjusted.",
                      duration: 8000,
                      type: "success"
                    });
                    props.getWaitlists();
                    setModalContent(null);
                    toggle();
                  })
                  .catch((error) => {
                    toast.show({
                      title: "Update Failed",
                      content: error.response?.data?.message ?? "Something went wrong, please try again later",
                      duration: 10000,
                      type: "fail"
                    });
                  })
                  .finally(() => {
                    setIsSavingDateReceived(false);
                  });
              }}
            >
              {isSavingDateReceived ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      );
    }

    return (
      <Confirm
        message={getMessage()}
        buttonsDisabled={approvingWithoutPayment}
        onConfirm={() => {
          if (modalContent === ConfirmModalType.ApproveWithoutPayment) {
            setApprovingWithoutPayment(true);
            apiService.approveWaitlistWithoutPayment(
              props.value.waitlist.event_id,
              props.value.waitlist._id,
              Array.from(selectedForApproval),
              !!overCapacityReason
            )
              .then(() => {
                toast.show({
                  title: "Approve Without Payment",
                  content: "You've successfully processed the selected waitlisted entr" + (selectedForApproval.size + 1 === 1 ? "y" : "ies") + ".",
                  duration: 10000,
                  type: "success"
                });
                props.getWaitlists();
                setModalContent(null);
                setAffectedWaitlists([]);
                setSelectedForApproval(new Set());
                setOverCapacityReason(null);
                toggle();
              })
              .catch((error) => {
                if (error.response?.data?.eagerdog_error_code === EagerdogErrors.REGISTRATION.SHOW_OVER_CAPACITY) {
                  // Show the capacity and let the club manager decide.
                  setOverCapacityReason(error.response?.data?.message ?? "This show is at capacity.");
                  return;
                }
                toast.show({
                  title: "Approve Without Payment",
                  content: error.response?.data?.message ?? "Something went wrong, please try again later",
                  duration: 10000,
                  type: "fail"
                });
              })
              .finally(() => {
                setApprovingWithoutPayment(false);
              });
            return;
          }

          if (modalContent === ConfirmModalType.Cancel) {
            if (props.value.waitlist.lottery_eligible) {
              apiService.rejectLotteryWaitlist(props.value.waitlist.event_id, props.value.waitlist._id)
                .then(() => {
                  toast.show({
                    title: "Cancel Waitlist Registration",
                    content: "You've successfully canceled "+ props.value.waitlist.call_name +" (" + props.value.waitlist.registered_name +")'s waitlist registration",
                    duration: 10000,
                    type: "success"
                  });
                  props.getWaitlists();
                  setModalContent(null);
                  toggle();
                })
                .catch((error) => {
                  toast.show({
                    title: "Waitlist Withdraw",
                    content: error.response?.data?.message ?? "Something went wrong, please try again later",
                    duration: 10000,
                    type: "fail"
                  });
                })
              return;
            }

            apiService.cancelEventWaitlistEntry(props.value.waitlist.event_id, props.value.waitlist._id).then((response) => {
              toast.show({
                title: "Cancel Waitlist Registration",
                content: "You've successfully canceled "+ props.value.waitlist.call_name +" (" + props.value.waitlist.registered_name +")'s waitlist registration",
                duration: 10000,
                type: "success"
              });

              props.getWaitlists();
              setModalContent(null);
              toggle();
            }).catch((e: any) => {
              toast.show({
                title: "Waitlist Withdraw",
                content: e.response.data.message ? e.response.data.message : "Something went wrong, please try again later",
                duration: 10000,
                type: "fail"
              });
            });
            return;
          }

          if (modalContent === ConfirmModalType.Delete) {
            apiService.deleteEventWaitlistEntry(props.value.waitlist.event_id, props.value.waitlist._id).then(() => {
              toast.show({
                title: "Delete Waitlist Registration",
                content: "You've successfully deleted "+ props.value.waitlist.call_name +" (" + props.value.waitlist.registered_name +")'s waitlist registration",
                duration: 10000,
                type: "success"
              });

              props.getWaitlists();
              setModalContent(null);
              toggle();
            }).catch((e: any) => {
              toast.show({
                title: "Waitlist Withdraw",
                content: e.response.data.message ? e.response.data.message : "Something went wrong, please try again later",
                duration: 10000,
                type: "fail"
              });
            });
            return;
          }
        }}
        onCancel={() => {
          setModalContent(null);
          setAffectedWaitlists([]);
          setSelectedForApproval(new Set());
          setOverCapacityReason(null);
          toggle();
        }}
      />
    );
  };

  return (<div className={styles.ActionRenderer}>
    <ContextMenu
      options={options}
    />
    <Modal
      className={
        modalContent === ConfirmModalType.EditDateReceived
          ? "editDateReceivedModal"
          : modalContent === ConfirmModalType.ApproveWithoutPayment
          ? "confirmModal approveWithoutPaymentModal"
          : "confirmModal"
      }
      isShown={isShown}
      hide={() => { setModalContent(null); setOverCapacityReason(null); toggle(); }}
      modalContent={getModalContent()}
    />
    {batchApproveOpen && props.event && (
      <BatchApproveModal
        isShown={batchApproveOpen}
        anchorWaitlist={props.value.waitlist}
        event={props.event}
        onClose={() => setBatchApproveOpen(false)}
        onApproved={() => { props.getWaitlists(); }}
      />
    )}
  </div>);
};
